import { useEffect, useRef } from 'react';
import { sanitizeMotionScene } from '@/lib/motion/sanitizeMotionScene';
import type { SubtitleEditorData } from '@/components/SubtitleEditor';
import type { MediaLayer } from '@/lib/layers/types';
import type { VideoEffects } from '@/lib/video-effects/types';
import type { KeyframeTransition, SceneTransition } from '@/lib/transitions/types';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import type { ClipCoverImage } from '@/lib/cover/coverImage';
import type { Scene } from '@/lib/ai-assistant/editor/sceneEdits';
import { buildLayoutCatalog } from '@/lib/ai-assistant/editor/layoutCatalog';
import type { AssistantMediaItem } from '@/lib/ai-assistant/editor/mediaTypes';
import { formatMediaResults } from '@/lib/ai-assistant/editor/mediaTools';
import { buildFrameCover } from '@/lib/cover/coverImage';
import type { AssistantMediaQuery } from './useAssistantMediaSearch';
import { useAssistantPanel } from '@/contexts/AssistantContext';
import { canApplyBrandKit } from '@/lib/brand-kit/brandKitAccess';
import { hydrateBrandKitWorkspace } from '@/lib/brand-kit/hydrateBrandKitWorkspace';
import type { ClientCall, ClientResult } from '@/lib/ai-assistant/clientTools';
import { buildEditorToolDefinitions } from '@/lib/ai-assistant/editor/editorTools';
import {
  applyEditorCalls,
  type EditorDraft,
  type EditorEnv,
} from '@/lib/ai-assistant/editor/applyEditorCalls';
import {
  buildClipSilences,
  buildClipTranscript,
  buildEditorPageState,
  buildTranscriptContext,
  fitPageState,
  type TimelineSpan,
  type TranscriptWord,
} from '@/lib/ai-assistant/editor/buildEditorPageState';
import {
  formatFrameClock,
  orderResults,
  splitCaptureCalls,
} from '@/lib/ai-assistant/editor/frameCapture';
import { SFX_CATEGORIES } from '@/lib/ai-assistant/editor/soundEffects';
import type { CapturedFrame } from './useEditorFrameCapture';

// Lets the AI assistant see and operate the clip editor. Edits go through the
// editor's own commit paths (subtitle state, useMediaLayers, effects and
// timeline commits), so preview, autosave, page-hide flush and export behave
// exactly as for a manual edit. Each batch can be undone from the panel.

interface BrandKit {
  id: string;
  name?: string | null;
  data?: unknown;
  is_public?: boolean | null;
}

export interface EditorAssistantBridgeInput {
  /** The clip's data finished loading for `clipKey`. */
  ready: boolean;
  clipKey: string | null;
  clipTitle: string | null;
  clipIndex: number;
  clipTitles: Array<string | null>;
  clipDuration: number;
  playhead: number;
  aspect: 'landscape' | 'portrait';
  words: readonly TranscriptWord[];
  intervals: readonly TimelineSpan[];
  /** The natural timeline (source ranges in playback order). */
  scenes: readonly Scene[];
  sourceDuration: number;
  /** The framing the preview applies (edited or auto-derived). */
  cropKeyframes: readonly CropCenterKeyframe[];
  /** Glides at framing boundaries inside scenes (camera moves). */
  keyframeTransitions: readonly KeyframeTransition[];
  /** The preview crops the source, so per-scene framing applies. */
  framingAvailable: boolean;
  cover: ClipCoverImage | null;
  /** A campaign enforces the layout: editor_set_layout is refused. */
  layoutEnforced: boolean;
  subtitle: SubtitleEditorData;
  subtitlesEnabled: boolean;
  fontId: string;
  fonts: ReadonlyArray<{ id: string; name: string; file_url?: string | null }>;
  kits: readonly BrandKit[];
  selectedKitId: string | null;
  hasProFeatures: boolean;
  layers: readonly MediaLayer[];
  videoEffects: VideoEffects;
  transitions: readonly SceneTransition[];
  postLayout: PostLayoutConfig | null;
  layoutFieldBinds: unknown;
  setSubtitleData: (next: SubtitleEditorData) => void;
  setFontId: (fontId: string) => void;
  setSubtitlesEnabled: (enabled: boolean) => void;
  commitLayers: (next: MediaLayer[]) => void;
  commitVideoEffects: (next: VideoEffects) => void;
  commitTransitions: (next: SceneTransition[]) => void;
  applyTemplate: (kitId: string) => void;
  restoreTemplate: (snapshot: { kitId: string | null; postLayout: PostLayoutConfig | null; fieldBinds: unknown }) => void;
  seekClipTime: (clipTime: number, play: boolean) => void;
  selectClip: (index: number) => void;
  /** The editor's raw timeline state (intervals, transitions, sequence flag,
   *  user-edited framing), for undo. Opaque to the bridge. */
  timelineSnapshot: () => unknown;
  restoreTimeline: (snapshot: unknown) => void;
  /** Commit a new natural timeline with its transitions and framing. */
  commitTimeline: (next: { scenes: Scene[]; transitions: SceneTransition[]; cropKeyframes: CropCenterKeyframe[] }) => void;
  setEditedCropKeyframes: (next: CropCenterKeyframe[]) => void;
  setKeyframeTransitions: (next: KeyframeTransition[]) => void;
  /** Layout state (layout, binds, speaker layout, preview aspect, subtitle), for undo. */
  layoutSnapshot: () => unknown;
  restoreLayout: (snapshot: unknown) => void;
  /** The layout picker's path (binds, preview aspect, speaker layout). */
  selectLayout: (kind: string, preset?: string) => void;
  setLayoutField: (key: string, value: string | number) => void;
  clearLayout: () => void;
  setCover: (cover: ClipCoverImage | null) => void;
  searchMedia: (query: AssistantMediaQuery) => Promise<AssistantMediaItem[]>;
  trackStockUse: (item: AssistantMediaItem) => void;
  /** editor_set_cover: the frame at a clip time, full size, uploaded. */
  captureCover: (time: number) => Promise<{ url: string; sourceTime: number } | null>;
  /** editor_capture_frames: frames as the preview draws them, uploaded. */
  captureFrames: (times: unknown) => Promise<CapturedFrame[]>;
  captureCurrentFrame: () => Promise<CapturedFrame | null>;
}

const CLIP_SWITCH_WAIT_MS = 8000;
// The backend drops a page state over 32 000 chars whole; keep a margin.
const PAGE_STATE_MAX_CHARS = 31000;
// Captures after edits of the same batch wait for the preview to re-render.
const SETTLE_BEFORE_CAPTURE_MS = 150;

const captureResult = (callId: string, frames: CapturedFrame[]): ClientResult => {
  const images = frames.flatMap((frame) => ('url' in frame ? [frame.url] : []));
  if (!images.length) {
    return { id: callId, ok: false, error: 'capture_failed', message: 'No frame could be rendered or uploaded.' };
  }
  return { id: callId, ok: true, result: { frames }, images };
};

const SEARCH_TOOL = 'editor_search_media';
const MEDIA_KINDS = ['image', 'video', 'audio'] as const;
const SEARCH_SOURCES = ['stock', 'my_media', 'sfx'] as const;

async function runSearch(
  call: ClientCall,
  input: EditorAssistantBridgeInput,
  remember: (item: AssistantMediaItem) => void,
): Promise<ClientResult> {
  const args = call.args && typeof call.args === 'object' ? call.args : {};
  const kind = MEDIA_KINDS.find((value) => value === args.kind);
  if (!kind) return { id: call.id, ok: false, error: 'invalid_option', message: 'kind: image, video or audio' };
  // Audio without a source means the sound-effect library.
  const source = SEARCH_SOURCES.find((value) => value === args.source) ?? (kind === 'audio' ? 'sfx' : 'stock');
  if (source === 'sfx' && kind !== 'audio') {
    return { id: call.id, ok: false, error: 'invalid_option', message: 'source sfx has only audio: use kind audio' };
  }
  const category = SFX_CATEGORIES.find((value) => value === args.category);
  if (args.category !== undefined && args.category !== null && !category) {
    return { id: call.id, ok: false, error: 'invalid_option', message: `category: ${SFX_CATEGORIES.join(', ')}` };
  }
  const query = typeof args.query === 'string' ? args.query.slice(0, 100) : '';
  try {
    const items = await input.searchMedia({ kind, query, source, ...(category && source === 'sfx' ? { category } : {}) });
    items.forEach(remember);
    const { items: rows, images } = formatMediaResults(items);
    return { id: call.id, ok: true, result: { items: rows }, ...(images.length ? { images } : {}) };
  } catch (error) {
    if (error instanceof Error && error.message === 'stock_has_no_audio') {
      return { id: call.id, ok: false, error: 'stock_has_no_audio', message: 'Stock has no audio: use source sfx or my_media' };
    }
    return { id: call.id, ok: false, error: 'search_failed' };
  }
}

const draftOf = (input: EditorAssistantBridgeInput): EditorDraft => ({
  subtitle: input.subtitle,
  subtitlesEnabled: input.subtitlesEnabled,
  fontId: input.fontId,
  layers: [...input.layers],
  videoEffects: input.videoEffects,
  transitions: [...input.transitions],
  scenes: input.scenes.map((scene) => ({ ...scene })),
  cropKeyframes: [...input.cropKeyframes],
  keyframeTransitions: [...input.keyframeTransitions],
  cover: input.cover,
});

const fontName = (input: EditorAssistantBridgeInput, id: string) => (
  input.fonts.find((font) => font.id === id)?.name ?? (id || null)
);

const LAYOUT_CATALOG = buildLayoutCatalog();

function envOf(input: EditorAssistantBridgeInput, media: ReadonlyMap<string, AssistantMediaItem>): EditorEnv {
  // New scene ids follow the timeline's own rule (above every id, and Date.now()).
  let lastSceneId = Math.max(Date.now(), ...input.scenes.map((scene) => scene.id));
  return {
    words: input.words,
    sourceDuration: input.sourceDuration,
    framingAvailable: input.framingAvailable,
    nextSceneId: () => { lastSceneId += 1; return lastSceneId; },
    layoutCatalog: LAYOUT_CATALOG,
    layoutEnforced: input.layoutEnforced,
    media,
    compAspect: input.aspect === 'portrait' ? 1080 / 1920 : 1920 / 1080,
    clipCount: input.clipTitles.length,
    fonts: input.fonts.map((font) => ({ id: font.id, name: font.name, fileUrl: font.file_url ?? null })),
    templates: input.kits.map((kit) => {
      const workspace = hydrateBrandKitWorkspace(kit.data);
      return {
        id: kit.id,
        name: kit.name || kit.id,
        canApply: canApplyBrandKit({ hasProFeatures: input.hasProFeatures, kit: kit as never }),
        subtitle: workspace.subtitle,
        fontId: workspace.fontId,
      };
    }),
    newId: () => crypto.randomUUID().slice(0, 8),
  };
}

function stateOf(input: EditorAssistantBridgeInput): Record<string, unknown> {
  return buildEditorPageState({
    clip: {
      title: input.clipTitle,
      index: input.clipIndex,
      duration: input.clipDuration,
      playhead: input.playhead,
      aspect: input.aspect === 'portrait' ? 'vertical' : 'horizontal',
    },
    clips: input.clipTitles.map((title) => ({ title })),
    transcript: buildClipTranscript(input.words, input.intervals),
    scenes: input.scenes,
    subtitle: input.subtitle,
    subtitlesEnabled: input.subtitlesEnabled,
    fontName: fontName(input, input.fontId),
    layers: input.layers,
    videoEffects: input.videoEffects,
    transitions: input.transitions,
    templateName: input.kits.find((kit) => kit.id === input.selectedKitId)?.name ?? null,
    fonts: input.fonts.map((font) => font.name),
    templates: input.kits.map((kit) => ({ id: kit.id, name: kit.name || kit.id })),
    pro: input.hasProFeatures,
    framingAvailable: input.framingAvailable,
    cropKeyframes: input.cropKeyframes,
    keyframeTransitions: input.keyframeTransitions,
    silences: buildClipSilences(input.words, input.intervals),
    context: buildTranscriptContext(input.words, input.scenes),
    layout: input.postLayout
      ? { kind: input.postLayout.kind, preset: (input.postLayout as { preset?: string | null }).preset ?? null }
      : null,
    layoutCatalog: LAYOUT_CATALOG,
    cover: input.cover,
  });
}

export function useEditorAssistantBridge(input: EditorAssistantBridgeInput) {
  const { registerPageBridge } = useAssistantPanel();
  const inputRef = useRef(input);
  inputRef.current = input;
  // Media found by editor_search_media in this page session, by item id.
  const mediaRef = useRef(new Map<string, AssistantMediaItem>());

  const { ready, clipKey } = input;
  useEffect(() => {
    if (!ready || !clipKey) return undefined;
    const waitForClip = async (index: number) => {
      const started = Date.now();
      while (Date.now() - started < CLIP_SWITCH_WAIT_MS) {
        const current = inputRef.current;
        if (current.ready && current.clipIndex === index && current.clipKey !== clipKey) return true;
         
        await new Promise<void>((resolve) => { setTimeout(resolve, 150); });
      }
      return false;
    };

    return registerPageBridge({
      kind: 'editor',
      key: clipKey,
      getTools: buildEditorToolDefinitions,
      getState: () => fitPageState(stateOf(inputRef.current), PAGE_STATE_MAX_CHARS),
      async execute(calls: ClientCall[]) {
        const { capture, edits: rest } = splitCaptureCalls(calls);
        const searches = rest.filter((call) => call.tool_name === SEARCH_TOOL);
        const edits = rest.filter((call) => call.tool_name !== SEARCH_TOOL);
        const searchResults: ClientResult[] = [];
        for (const call of searches) {
          searchResults.push(await runSearch(call, inputRef.current, (item) => mediaRef.current.set(item.id, item)));
        }
        // The user may switch clips while a call waits (search, capture):
        // nothing may then be written to the clip that is open now.
        const sameClip = () => inputRef.current.clipKey === clipKey;
        const clipChanged = (id: string): ClientResult => ({
          id, ok: false, error: 'clip_changed', message: 'The user opened another clip meanwhile.',
        });
        if (!sameClip()) {
          return { results: orderResults(calls, [...searchResults, ...edits.map((call) => clipChanged(call.id)), ...capture.map((call) => clipChanged(call.id))]) };
        }

        const current = inputRef.current;
        const before = draftOf(current);
        const templateBefore = {
          kitId: current.selectedKitId,
          postLayout: current.postLayout,
          fieldBinds: current.layoutFieldBinds,
        };
        const timelineBefore = current.timelineSnapshot();
        const layoutBefore = current.layoutSnapshot();
        const outcome = applyEditorCalls(before, edits, envOf(current, mediaRef.current));
        const { draft, changed } = outcome;

        if (outcome.templateId) current.applyTemplate(outcome.templateId);
        if (changed.has('timeline')) {
          current.commitTimeline({ scenes: draft.scenes, transitions: draft.transitions, cropKeyframes: draft.cropKeyframes });
        } else if (changed.has('transitions')) {
          current.commitTransitions(draft.transitions);
        }
        if (changed.has('framing')) {
          current.setEditedCropKeyframes(draft.cropKeyframes);
          current.setKeyframeTransitions(draft.keyframeTransitions);
        }
        if (outcome.layout) {
          if (outcome.layout.kind === 'none') {
            current.clearLayout();
          } else {
            current.selectLayout(outcome.layout.kind, outcome.layout.preset);
            outcome.layout.fields.forEach((field) => {
              current.setLayoutField(field.key, field.value);
              if (field.urlKey) current.setLayoutField(field.urlKey, field.url ?? '');
            });
          }
        }
        if (changed.has('subtitle')) {
          current.setSubtitleData(draft.subtitle);
          if (draft.fontId !== before.fontId) current.setFontId(draft.fontId);
          if (draft.subtitlesEnabled !== before.subtitlesEnabled) {
            current.setSubtitlesEnabled(draft.subtitlesEnabled);
          }
        }
        if (changed.has('layers')) current.commitLayers(draft.layers);
        if (changed.has('effects')) current.commitVideoEffects(draft.videoEffects);
        edits.forEach((call) => {
          if (call.tool_name !== 'editor_add_media') return;
          const added = outcome.results.find((result) => result.id === call.id);
          const item = mediaRef.current.get(String(call.args?.item_id ?? ''));
          if (added?.ok && item) current.trackStockUse(item);
        });
        if (changed.has('layers')) {
          const sources = (layers: typeof draft.layers) => {
            const urls = new Set<string>();
            const visit = (elements: import('@/lib/motion/types').MotionElement[]) => {
              elements.forEach((element) => {
                if (element.src) urls.add(element.src);
                if (element.children) visit(element.children);
              });
            };
            layers.forEach((layer) => {
              if (layer.type === 'motion') {
                const scene = sanitizeMotionScene(layer.scene).scene;
                if (scene) visit(scene.elements);
              }
            });
            return urls;
          };
          const previous = sources(before.layers);
          const added = sources(draft.layers);
          mediaRef.current.forEach((item) => {
            if (added.has(item.url) && !previous.has(item.url)) current.trackStockUse(item);
          });
        }
        if (outcome.seek) current.seekClipTime(outcome.seek.time, outcome.seek.play);

        let results: ClientResult[] = outcome.results;
        if (outcome.selectClipIndex !== null) {
          current.selectClip(outcome.selectClipIndex);
          const switched = await waitForClip(outcome.selectClipIndex);
          if (!switched) {
            results = results.map((result) => (result.ok
              ? { ...result, ok: false, error: 'clip_not_loaded' }
              : result));
          }
        }

        const edited = changed.size > 0 || !!outcome.templateId || !!outcome.layout;
        const settle = () => new Promise<void>((resolve) => { setTimeout(resolve, SETTLE_BEFORE_CAPTURE_MS); });
        let coverChanged = false;
        if (outcome.cover) {
          const { time, callId } = outcome.cover;
          if (edited) await settle();
          const shot = sameClip() ? await inputRef.current.captureCover(time) : null;
          if (!sameClip()) {
            results = results.map((result) => (result.id === callId ? clipChanged(callId) : result));
          } else if (shot) {
            inputRef.current.setCover(buildFrameCover(shot.url, shot.sourceTime));
            coverChanged = true;
          } else {
            results = results.map((result) => (result.id === callId
              ? { id: callId, ok: false, error: 'capture_failed', message: 'The frame could not be rendered or uploaded.' }
              : result));
          }
        }

        if (capture.length) {
          if (edited || outcome.seek || outcome.selectClipIndex !== null) await settle();
          for (const call of capture) {
            if (!sameClip() && outcome.selectClipIndex === null) {
              results = [...results, clipChanged(call.id)];
              continue;
            }
            const frames = await inputRef.current.captureFrames(call.args?.times);
            results = [...results, captureResult(call.id, frames)];
          }
        }
        results = orderResults(calls, [...searchResults, ...results]);

        // Undo belongs to this clip: none once the user is on another one.
        if ((!edited && !coverChanged) || !sameClip()) return { results };
        const undo = () => {
          const latest = inputRef.current;
          if (outcome.templateId) latest.restoreTemplate(templateBefore);
          if (outcome.layout) latest.restoreLayout(layoutBefore);
          if (changed.has('subtitle')) {
            latest.setSubtitleData(before.subtitle);
            if (draft.fontId !== before.fontId) latest.setFontId(before.fontId);
            if (draft.subtitlesEnabled !== before.subtitlesEnabled) {
              latest.setSubtitlesEnabled(before.subtitlesEnabled);
            }
          }
          if (changed.has('timeline') || changed.has('transitions') || changed.has('framing')) {
            latest.restoreTimeline(timelineBefore);
          }
          if (changed.has('layers')) latest.commitLayers(before.layers);
          if (changed.has('effects')) latest.commitVideoEffects(before.videoEffects);
          if (coverChanged) latest.setCover(before.cover);
        };
        return { results, undo };
      },
      async captureFrame() {
        const frame = await inputRef.current.captureCurrentFrame();
        return frame && 'url' in frame ? { url: frame.url, name: `Frame ${formatFrameClock(frame.time)}` } : null;
      },
    });
  }, [ready, clipKey, registerPageBridge]);
}
